/**
 * Format helpers used across the app.
 * Keep these pure — no React, no DOM.
 */

/**
 * Format a number as currency.
 *
 * Phase 20.3: accepts an optional `currency` parameter (ISO 4217 code).
 * Defaults to USD for backward compatibility with call sites that don't
 * yet pass the workspace currency.
 *
 * The `compact` flag uses Intl's built-in compact notation, which is
 * locale- and currency-aware (e.g., "$1.5M", "€1,5 Mio.", "¥150万").
 * We use 'en-US' locale to keep formatting consistent for English-speaking
 * business users; full per-locale formatting is a future enhancement.
 */
export function formatCurrency(
  amount: number,
  opts?: { compact?: boolean; currency?: string },
): string {
  const currency = opts?.currency || 'USD';
  if (opts?.compact) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(amount);
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatDate(iso: string | undefined, opts?: { withTime?: boolean }): string {
  if (!iso) return '—';
  // A date-only string ("YYYY-MM-DD") is a calendar date with no timezone.
  // `new Date('2026-06-10')` parses it as UTC midnight, which toLocaleDateString
  // then renders a day earlier in negative-UTC timezones (US etc.). Parse
  // date-only values as LOCAL midnight so the displayed day matches the stored
  // day. Real timestamps (with a time component) are left untouched.
  const trimmed = iso.trim();
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
  const d = new Date(isDateOnly ? `${trimmed}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  if (opts?.withTime) {
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a date with custom Intl options (e.g. month/day without a year).
 * Parses date-only strings ("YYYY-MM-DD") as LOCAL midnight so they don't shift
 * a day earlier in negative-UTC timezones — the same fix as formatDate, for the
 * call sites that need a shorter format than formatDate provides.
 */
export function formatDateParts(
  iso: string | undefined | null,
  options: Intl.DateTimeFormatOptions,
): string {
  if (!iso) return '';
  const trimmed = iso.trim();
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
  const d = new Date(isDateOnly ? `${trimmed}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', options);
}

export function formatPercent(n: number): string {
  return `${Math.round(n)}%`;
}

export function daysBetween(a: string, b: string): number | null {
  if (!a || !b) return null;
  const da = new Date(a + 'T00:00:00');
  const db = new Date(b + 'T00:00:00');
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return null;
  return Math.round((db.getTime() - da.getTime()) / (1000 * 60 * 60 * 24));
}
